import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { Box, Stack, TextField, Typography, useTheme } from "@mui/material";
import { DekoxIcon } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { addEditConfigurationDialogActions } from "src/pages/configurator/components/add-edit-configuration-dialog/add-edit-configuration-dialog-store";
import { configureProductActions } from "../../configure-product-store";
import { getProjectLineItemsHierarchy } from "src/pages/configurator/configurator-utils";
import { ProductsListSectionLoading } from "./products-list-section-loading";
import { useParams, useSearchParams } from "react-router-dom";
import { TConfigureProductSearchParams } from "../../configure-product-types";
import { useEffectAfterMount } from "src/utils/hooks/useEffectAfterMount";
import { getClickableProps } from "src/utils/keyboard-utils";
import { LabelsMultiSelectChip } from "src/pages/configurator/components";
import { LabelsMultiSelectChipRef } from "src/pages/configurator/components/labels-multi-select-chip/labels-multi-select-chip-types";

const mapStateToProps = (state: RootState) => ({
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog,
  configurationState: state.pageReducer.configureProductReducer.configurationState,
  selectedProjectLineItemId: state.pageReducer.configureProductReducer.selectedProjectLineItemId
});

const ownActions = {
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  setFormData: addEditConfigurationDialogActions.setFormData,
  setSelectedProjectLineItemId: configureProductActions.setSelectedProjectLineItemId,
  updateProjectLineItemRequest: configureProductActions.updateProjectLineItemRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const ProductListSectionBase = (props: Props) => {
  const { setAddEditConfigurationDialog, configurationState, setSelectedProjectLineItemId, setFormData, selectedProjectLineItemId, updateProjectLineItemRequest } = props;

  const theme = useTheme();

  const [searchParams] = useSearchParams();
  const { projectId = "" } = useParams<{ projectId: string }>();

  const genericTranslationsT = useGenericTranslationsT();
  const configuratorPageT = useConfiguratorPageT();

  const [configuratorName, setConfiguratorName] = useState("");
  const [isModifiedLabels, setIsModifiedLabels] = useState(false);
  const [isLabelsLoading, setIsLabelsLoading] = useState(false);

  const isInitializingName = useRef(false);
  const labelsMultiChipRef = useRef<LabelsMultiSelectChipRef>(null);

  // Initialize configurator name
  useEffect(() => {
    if (configurationState.loading === "has-loaded") {
      if (!configuratorName) {
        isInitializingName.current = true;
        setConfiguratorName(configurationState.data.configurationName);
      }
    }
  }, [configurationState]);

  // debounce and save configurationName
  useEffectAfterMount(() => {
    if (isInitializingName.current) {
      isInitializingName.current = false;
      return;
    }
    const timer = setTimeout(() => {
      saveconfigurationName();
    }, 1000);

    return () => clearTimeout(timer);
  }, [configuratorName]);

  // Initialize selectedProjectLineItemId
  useEffect(() => {
    if (configurationState.loading === "has-loaded") {
      const index = configurationState.data.projectLineItems.findIndex(
        projectLineItem =>
          projectLineItem.projectLineItemId === selectedProjectLineItemId ||
          (!selectedProjectLineItemId && projectLineItem.projectLineItemId === searchParams.get("projectLineItemId" as keyof TConfigureProductSearchParams))
      );
      setSelectedProjectLineItemId(configurationState.data.projectLineItems?.[index >= 0 ? index : 0]?.projectLineItemId);
    }
  }, [configurationState, selectedProjectLineItemId]);

  const saveconfigurationName = () => {
    if (configurationState.loading === "has-loaded") {
      const firstProjectLineItemId = configurationState.data.projectLineItems.find(projectLineItem => projectLineItem.parentProjectLineItemId === null)?.projectLineItemId;
      if (!firstProjectLineItemId) return;
      updateProjectLineItemRequest({ projectId: configurationState.data.projectId, projectLineItemId: firstProjectLineItemId, description: configuratorName });
    }
  };

  const lastProjectLineItem = getProjectLineItemsHierarchy(configurationState.loading === "has-loaded" ? configurationState.data.projectLineItems : []).last();

  const crossAndCheckBtnSx = {
    boxShadow: 3,
    borderRadius: 2,
    background: theme.palette.customColors.white,
    border: `1px solid ${theme.palette.customColors.border}`,
    color: theme.palette.success.main,
    position: "absolute",
    bottom: -34,
    right: 0,
    zIndex: 99,
    "&:hover, &.Mui-disabled": {
      background: theme.palette.customColors.white
    }
  };

  const handleAddProduct = () => {
    if (!lastProjectLineItem) return;

    setFormData({
      productsFilter: {
        parentProjectLineItemId: lastProjectLineItem.projectLineItemId,
        productTypes: [lastProjectLineItem.nextProductTypeId || ""],
        systemPackageId: lastProjectLineItem.systemPackageId
      }
    });
    setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
  };

  if (configurationState.loading === "is-loading") return <ProductsListSectionLoading />;

  if (configurationState.loading === "has-loaded")
    return (
      <Stack gap={4}>
        {/* Title */}
        <Typography variant="h2" fontWeight="normal" color={theme.palette.primary.contrastText}>
          {configuratorPageT.configuration}
        </Typography>

        {/* Configurator name and label */}
        <Stack gap={1}>
          <TextField
            autoFocus
            value={configuratorName}
            onChange={e => {
              setConfiguratorName(e.target.value);
            }}
            size="small"
            label={genericTranslationsT.name}
            sx={{
              fontSize: theme.typography.caption.fontSize,
              "& .MuiOutlinedInput-root": {
                bgcolor: theme.palette.customColors.whiteFaint,
                color: theme.palette.primary.contrastText,
                "& fieldset": { borderColor: theme.palette.customColors.whiteBorder },
                "&:hover fieldset": { borderColor: theme.palette.customColors.whiteStrong },
                "&.Mui-focused fieldset": { borderColor: theme.palette.primary.contrastText }
              },
              "& .MuiInputLabel-root": {
                color: theme.palette.customColors.whiteStrong,
                "&.Mui-focused": { color: theme.palette.primary.contrastText }
              }
            }}
            onKeyDown={e => e.key === "Enter" && saveconfigurationName()}
          />
          <Box position="relative">
            <LabelsMultiSelectChip
              key={configurationState.data.configurationId}
              ref={labelsMultiChipRef}
              projectId={projectId}
              entityIds={[configurationState.data.configurationId || ""]}
              onLoadingChange={setIsLabelsLoading}
              onChange={() => {
                setIsModifiedLabels(true);
              }}
              darkMode
              floatingLabelBg={theme.palette.primary.main}
            />

            {isModifiedLabels && (
              <>
                <IconButton
                  k={genericTranslationsK.done}
                  onClick={e => {
                    e.stopPropagation();
                    labelsMultiChipRef.current?.handleApply(() => {
                      setIsModifiedLabels(false);
                    });
                  }}
                  size="small"
                  loading={isLabelsLoading}
                  sx={{ ...crossAndCheckBtnSx, right: 34 }}
                >
                  <DekoxIcon icon="check" fontSize="small" />
                </IconButton>
                <IconButton
                  disabled={isLabelsLoading}
                  color="error"
                  k={genericTranslationsK.cancel}
                  onClick={e => {
                    e.stopPropagation();
                    setIsModifiedLabels(false);
                  }}
                  size="small"
                  sx={crossAndCheckBtnSx}
                >
                  <DekoxIcon icon="close" fontSize="small" color="error" />
                </IconButton>
              </>
            )}
          </Box>
        </Stack>

        {/* Products */}
        <Stack gap={2}>
          <Typography variant="body2" fontWeight="500" color={theme.palette.primary.contrastText}>
            {configuratorPageT.products}
          </Typography>
          <Stack gap={1}>
            {configurationState.data.projectLineItems.map(projectLineItem => {
              return (
                <Stack
                  key={projectLineItem.projectLineItemId}
                  p={1}
                  border={selectedProjectLineItemId === projectLineItem.projectLineItemId ? `2px solid ${theme.palette.primary.contrastText}` : `1px solid ${theme.palette.customColors.whiteMuted}`}
                  borderRadius={1}
                  bgcolor={theme.palette.customColors.whiteSurface}
                  {...getClickableProps(() => setSelectedProjectLineItemId(projectLineItem.projectLineItemId))}
                  sx={{
                    cursor: "pointer",
                    color: theme.palette.primary.contrastText,
                    outline: "none",
                    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.contrastText}` }
                  }}
                  onClick={() => {
                    setSelectedProjectLineItemId(projectLineItem.projectLineItemId);
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="500">
                    {projectLineItem.productDescription} ({projectLineItem.productNumber})
                  </Typography>
                </Stack>
              );
            })}
            {lastProjectLineItem &&
              lastProjectLineItem.nextProductTypeName &&
              lastProjectLineItem.projectLineItemId &&
              lastProjectLineItem.nextProductTypeId &&
              lastProjectLineItem.systemPackageId && (
                <Box>
                  <TooltipShortcut shortcut={{ action: handleAddProduct, shortCutKey: "Insert" }}>
                    <Button
                      t={genericTranslationsT}
                      k={genericTranslationsK}
                      field="dynamicValue"
                      tArgs={[lastProjectLineItem.nextProductTypeName]}
                      variant="contained"
                      startIcon={<DekoxIcon icon="add" />}
                      onClick={handleAddProduct}
                      sx={{ bgcolor: theme.palette.customColors.white, color: theme.palette.primary.main, "&:hover": { bgcolor: theme.palette.customColors.whiteHover } }}
                    />
                  </TooltipShortcut>
                </Box>
              )}
          </Stack>
        </Stack>
      </Stack>
    );

  return <></>;
};

export const ProductsListSection = connect(mapStateToProps, ownActions)(ProductListSectionBase);
